import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type Route = components["schemas"]["Route"];
export type RouteTrack = components["schemas"]["RouteTrack"];
export type RouteStep = components["schemas"]["RouteStep"];
export type StepView = components["schemas"]["StepView"];
export type Content = components["schemas"]["Content"];
export type GateEval = components["schemas"]["GateEval"];
export type DashboardData = components["schemas"]["Dashboard"];
export type Tree = components["schemas"]["Tree"];
export type StepDoc = components["schemas"]["StepDoc"];
export type ImportRow = components["schemas"]["ImportRow"];
export type ImportPreview = components["schemas"]["ImportPreview"];
export type Usage = components["schemas"]["Usage"];
export type ApiError = components["schemas"]["Error"];

/** Ошибка API с кодом и человеческим сообщением (E1: причина и действие). */
export class RequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

function fail(status: number, error: unknown): never {
  const e = error as Partial<ApiError> | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? "Не удалось выполнить запрос.");
}

export const keys = {
  route: ["route"] as const,
  dashboard: ["dashboard"] as const,
  step: (id: string) => ["step", id] as const,
  tree: ["admin", "tree"] as const,
  draft: (id: string) => ["admin", "draft", id] as const,
  usage: (id: string) => ["admin", "usage", id] as const,
};

export function useRoute() {
  return useQuery({
    queryKey: keys.route,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/route");
      if (!data) fail(response.status, error);
      return data;
    },
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/dashboard");
      if (!data) fail(response.status, error);
      return data;
    },
  });
}

export function useStep(id: string) {
  return useQuery({
    queryKey: keys.step(id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/steps/{stepId}", { params: { path: { stepId: id } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: (n, err) => !(err instanceof RequestError && (err.status === 403 || err.status === 404)) && n < 2,
  });
}

export type TickKind = "unit" | "task" | "resource" | "quiz" | "extra";

const tickList: Record<TickKind, keyof StepView["ticks"]> = { unit: "units", task: "tasks", resource: "resources", quiz: "quiz", extra: "extra" };

/** Отметка юнита/задачи/ресурса/вопроса «Проверь себя»/доп. задачи. Оптимистично: локальные ticks меняются
 *  сразу, оценка гейта приходит с сервера; при ошибке — откат. */
export function useTick(stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, key, on }: { kind: TickKind; key: string; on: boolean }) => {
      const params = { params: { path: { stepId, kind, key } } };
      const { data, error, response } = on ? await api.PUT("/steps/{stepId}/ticks/{kind}/{key}", params) : await api.DELETE("/steps/{stepId}/ticks/{kind}/{key}", params);
      if (!data) fail(response.status, error);
      return data;
    },
    onMutate: async ({ kind, key, on }) => {
      await qc.cancelQueries({ queryKey: keys.step(stepId) });
      const prev = qc.getQueryData<StepView>(keys.step(stepId));
      if (prev) {
        const list = tickList[kind];
        const set = new Set(prev.ticks[list]);
        if (on) set.add(key);
        else set.delete(key);
        qc.setQueryData<StepView>(keys.step(stepId), { ...prev, ticks: { ...prev.ticks, [list]: [...set] } });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.step(stepId), ctx.prev);
    },
    onSuccess: (gate) => {
      qc.setQueryData<StepView>(keys.step(stepId), (cur) => (cur ? { ...cur, gate } : cur));
      void qc.invalidateQueries({ queryKey: keys.dashboard });
      void qc.invalidateQueries({ queryKey: keys.route });
    },
  });
}

export function useSubmitGate(stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/steps/{stepId}/gate/submit", { params: { path: { stepId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.step(stepId) });
      void qc.invalidateQueries({ queryKey: keys.route });
      void qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

/* ── конструктор ─────────────────────────────────────────────── */

export function useTree() {
  return useQuery({
    queryKey: keys.tree,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/course");
      if (!data) fail(response.status, error);
      return data;
    },
    retry: (n, err) => !(err instanceof RequestError && err.status === 403) && n < 2,
  });
}

export function useDraft(id: string) {
  return useQuery({
    queryKey: keys.draft(id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/steps/{stepId}", { params: { path: { stepId: id } } });
      if (!data) fail(response.status, error);
      return data;
    },
  });
}

export function useUsage(id: string) {
  return useQuery({
    queryKey: keys.usage(id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/steps/{stepId}/usage", { params: { path: { stepId: id } } });
      if (!data) fail(response.status, error);
      return data;
    },
  });
}

export function useSaveDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title?: string; note?: string; content: Content }) => {
      const { data, error, response } = await api.PUT("/admin/steps/{stepId}", { params: { path: { stepId: id } }, body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: (doc) => {
      qc.setQueryData(keys.draft(id), doc);
      void qc.invalidateQueries({ queryKey: keys.tree });
    },
  });
}

export function usePublish(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/admin/steps/{stepId}/publish", { params: { path: { stepId: id } } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: (doc) => {
      qc.setQueryData(keys.draft(id), doc);
      void qc.invalidateQueries({ queryKey: keys.tree });
      void qc.invalidateQueries({ queryKey: keys.step(id) });
    },
  });
}

export function useApplyImport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: ImportRow[]) => {
      const { data, error, response } = await api.POST("/admin/steps/{stepId}/import/apply", { params: { path: { stepId: id } }, body: { rows } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: (doc) => qc.setQueryData(keys.draft(id), doc),
  });
}

/** Импорт — multipart вне типизированного клиента. */
export async function previewImport(stepId: string, file: File): Promise<ImportPreview> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/admin/steps/${stepId}/import`, { method: "POST", body: fd });
  const body = (await res.json()) as ImportPreview | ApiError;
  if (!res.ok) fail(res.status, body);
  return body as ImportPreview;
}
